import { CHART_DESK_STORAGE_KEYS } from "@/lib/localStorageKeys/chart-desk";
import {
  isChartPanelId,
  type ChartPanelId,
} from "@/vybe-trader-client/chart/stores/chartPanelTypes";
import type { TimeFrame } from "@/vybe-trader-client/chart/types";
import {
  CHART_DESK_VERSION,
  type ChartDeskSlot,
  type ChartDeskSnapshot,
} from "./types";

const TIMEFRAMES: readonly TimeFrame[] = [
  "1m",
  "5m",
  "15m",
  "30m",
  "1h",
  "2h",
  "4h",
  "12h",
  "1d",
  "1w",
  "1mn",
];

function isTimeFrame(v: unknown): v is TimeFrame {
  return typeof v === "string" && (TIMEFRAMES as readonly string[]).includes(v);
}

export function readDesk(): ChartDeskSnapshot | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(CHART_DESK_STORAGE_KEYS.CORE);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;

    const candidate = parsed as Partial<ChartDeskSnapshot>;
    if (candidate.v !== CHART_DESK_VERSION) return null;
    if (typeof candidate.accountKey !== "string" || !candidate.accountKey) {
      return null;
    }
    if (!candidate.slots || typeof candidate.slots !== "object") return null;

    // Re-validate every slot: a delisted symbol or a hand-edited record must not
    // be able to feed garbage into the chart store.
    const slots: Partial<Record<ChartPanelId, ChartDeskSlot>> = {};
    for (const [key, value] of Object.entries(candidate.slots)) {
      const panelId = Number(key);
      if (!Number.isInteger(panelId) || !isChartPanelId(panelId)) continue;
      if (!value || typeof value !== "object") continue;

      const slot = value as Partial<ChartDeskSlot>;
      if (typeof slot.symbol !== "string" || !slot.symbol.trim()) continue;
      if (!isTimeFrame(slot.timeframe)) continue;

      slots[panelId] = { symbol: slot.symbol.trim(), timeframe: slot.timeframe };
    }

    if (Object.keys(slots).length === 0) return null;

    return { v: CHART_DESK_VERSION, accountKey: candidate.accountKey, slots };
  } catch (error) {
    console.error("Error loading chart desk from localStorage:", error);
    return null;
  }
}

export function writeDesk(snapshot: ChartDeskSnapshot): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      CHART_DESK_STORAGE_KEYS.CORE,
      JSON.stringify(snapshot),
    );
  } catch (error) {
    console.error("Error saving chart desk to localStorage:", error);
  }
}

export function clearDesk(): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(CHART_DESK_STORAGE_KEYS.CORE);
  } catch (error) {
    console.error("Error clearing chart desk:", error);
  }
}
